import { CoachingFollowups } from "@/features/coaching-followups/CoachingFollowups";
import { CoachAssistPanel } from "../assist/CoachAssistPanel";
import { useId, useRef, useState } from "react";
import { draftContextKey, useComposerDraftContext } from "@/features/coach-review/composerDrafts";
import type { PlayerGoal } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { PlayerDevelopmentProfile } from "@/features/feedback/PlayerDevelopmentProfile";
import { GoalList } from "@/features/feedback/components/GoalList";
import { useCoachPlayerDevelopment } from "@/features/feedback/hooks/useDevelopmentProfile";
import { useCoachGoalMutations } from "../hooks/useCoachGoals";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Plus } from "lucide-react";

/**
 * Coach-side Player Development modal (M7) — opened from a Coach >
 * Roster row's "Development profile" button.
 *
 * Hosts the SHARED `<PlayerDevelopmentProfile>` painter with
 * `viewer={false}` — so it renders the coach-only Themes section
 * + linked accounts that the viewer Development sub-tab omits.
 *
 * Goal list: the painter's `goalsSlot` gets a coach-mode
 * `<GoalList>`. Status changes happen inline (a `<select>` →
 * `updateGoal`, no nested modal). But "+ New goal" / "Edit" need
 * the `GoalComposerModal`, which itself wraps `CoachModal` — and
 * two stacked `CoachModal`s would both grab Escape. So those two
 * bubble UP to the shell: it temporarily replaces this workspace
 * with the composer, then restores the same player plan afterward.
 *
 * Delete shows a `<ConfirmDialog>` — no nested modal stacking.
 *
 * Coach recent-note/clip/playlist rows are read-only (no
 * `onOpen*`) — the coach surface isn't a playback context.
 */

export type CoachDevelopmentModalProps = {
  playerId: string;
  /** Display name for the modal title while the profile loads;
   * the painter re-renders the full header from the payload. */
  playerName: string;
  onClose: () => void;
  /** Roster control that should regain focus after this workspace closes. */
  returnFocusTo?: HTMLElement | null;
  /** Open the goal composer in CREATE mode for this player —
   * the host closes this modal first (one-modal-at-a-time). */
  onNewGoal: (playerId: string) => void;
  /** Open the goal composer in EDIT mode — same one-modal rule. */
  onEditGoal: (goal: PlayerGoal) => void;
};

const WORKSPACE_TABS = [
  { id: "plan", label: "Plan" },
  { id: "timeline", label: "Timeline" },
  { id: "context", label: "Coaching context" },
] as const;
type WorkspaceTab = (typeof WORKSPACE_TABS)[number]["id"];

export function CoachDevelopmentModal(props: CoachDevelopmentModalProps) {
  const owner = useComposerDraftContext();
  // Tabs retain the same mounted forms. A real actor/scope/player change is
  // different: retire disclosures, pending confirmations and old form owners.
  return <OwnedDevelopmentModal key={`${draftContextKey(owner)}:${props.playerId}`} {...props} />;
}

function OwnedDevelopmentModal({
  playerId,
  playerName,
  onClose,
  returnFocusTo,
  onNewGoal,
  onEditGoal,
}: CoachDevelopmentModalProps) {
  const [tab, setTab] = useState<WorkspaceTab>("plan");
  const tabId = useId();
  const tabButtons = useRef<Array<HTMLButtonElement | null>>([]);
  const toast = useToast();
  const devQuery = useCoachPlayerDevelopment(playerId);
  const goalMutations = useCoachGoalMutations();
  const [confirmDeleteGoal, setConfirmDeleteGoal] = useState<PlayerGoal | null>(null);

  const handleStatusChange = async (goalId: number, status: string) => {
    // `status` is a closed enum from the card's <select>.
    try {
      await goalMutations.updateGoal(goalId, {
        status: status as PlayerGoal["status"],
      });
      toast.success("Goal updated");
    } catch (err) {
      toast.error("Couldn't update goal", { description: errorMessage(err) });
    }
  };

  const handleAckReflections = async (goal: PlayerGoal) => {
    try {
      await goalMutations.ackReflections(goal.id);
      toast.success("Marked seen");
    } catch (err) {
      toast.error("Couldn't mark it seen", { description: errorMessage(err) });
    }
  };

  const handleDelete = (goal: PlayerGoal) => {
    setConfirmDeleteGoal(goal);
  };

  const confirmDeleteGoalFn = async () => {
    if (!confirmDeleteGoal) return;
    const goal = confirmDeleteGoal;
    setConfirmDeleteGoal(null);
    try {
      await goalMutations.deleteGoal(goal.id);
      toast.success("Goal deleted");
    } catch (err) {
      toast.error("Couldn't delete goal", { description: errorMessage(err) });
    }
  };

  return (
    <>
      {confirmDeleteGoal ? (
        <ConfirmDialog
          title="Delete goal?"
          message={`Delete the goal "${confirmDeleteGoal.title || "Player goal"}"? This can't be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeleteGoalFn}
          onClose={() => setConfirmDeleteGoal(null)}
          data-testid="coach-development-delete-confirm"
        />
      ) : null}
      <CoachModal
        title={`Development profile · ${
          devQuery.data?.player.display_name || playerName
        }`}
        size="workspace"
        bodyClassName="bg-canvas"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onNewGoal(playerId)}
              data-testid="coach-development-new-goal"
            >
              <Plus aria-hidden="true" className="size-4" />
              New goal
            </Button>
            <Button type="button" onClick={onClose}>
              Done
            </Button>
          </div>
        }
        onClose={onClose}
        returnFocusTo={returnFocusTo}
        data-testid="coach-development-modal"
      >
        <div className="flex min-w-0 flex-col gap-5">
          <div role="tablist" aria-label="Player development" className="flex w-fit max-w-full gap-1 rounded-pill bg-elevated p-1">
            {WORKSPACE_TABS.map((item, index) => (
              <Button
                key={item.id}
                ref={element => { tabButtons.current[index] = element; }}
                type="button"
                role="tab"
                id={`${tabId}-${item.id}-tab`}
                aria-controls={`${tabId}-${item.id}-panel`}
                aria-selected={tab === item.id}
                tabIndex={tab === item.id ? 0 : -1}
                variant={tab === item.id ? "primary" : "ghost"}
                className="min-h-11 min-w-0 whitespace-normal rounded-pill px-3 text-sm"
                onClick={() => setTab(item.id)}
                onKeyDown={event => {
                  const next = event.key === "ArrowRight" ? (index + 1) % WORKSPACE_TABS.length
                    : event.key === "ArrowLeft" ? (index + WORKSPACE_TABS.length - 1) % WORKSPACE_TABS.length
                    : event.key === "Home" ? 0 : event.key === "End" ? WORKSPACE_TABS.length - 1 : null;
                  if (next === null) return;
                  event.preventDefault();
                  setTab(WORKSPACE_TABS[next].id);
                  tabButtons.current[next]?.focus();
                }}
              >{item.label}</Button>
            ))}
          </div>

          <div role="tabpanel" id={`${tabId}-plan-panel`} aria-labelledby={`${tabId}-plan-tab`} hidden={tab !== "plan"} tabIndex={0}>
            {devQuery.isLoading ? (
              <p className="text-ink-muted text-sm font-mono px-3 py-6" data-testid="coach-development-loading">Loading player plan…</p>
            ) : devQuery.isError ? (
              <p className="text-destructive font-mono text-sm px-3 py-6" data-testid="coach-development-error">Could not load the player plan.</p>
            ) : devQuery.data ? (
              <PlayerDevelopmentProfile
                profile={devQuery.data}
                viewer={false}
                presentation="coach-workspace"
                workspaceSection="plan"
                goalsSlot={<GoalList
                  goals={devQuery.data.active_goals}
                  viewer={false}
                  onChangeStatus={handleStatusChange}
                  onEdit={onEditGoal}
                  onDelete={handleDelete}
                  onAckReflections={handleAckReflections}
                />}
              />
            ) : null}
            <div className="mt-6">
              <CoachAssistPanel active={tab === "plan"} playerId={playerId} onNewGoal={onNewGoal} onEditGoal={onEditGoal} onBeforeOpenFeedback={onClose} />
            </div>
          </div>

          <div role="tabpanel" id={`${tabId}-timeline-panel`} aria-labelledby={`${tabId}-timeline-tab`} hidden={tab !== "timeline"} tabIndex={0}>
            <CoachingFollowups playerId={playerId} />
          </div>

          <div role="tabpanel" id={`${tabId}-context-panel`} aria-labelledby={`${tabId}-context-tab`} hidden={tab !== "context"} tabIndex={0}>
            {devQuery.isLoading ? (
              <p className="text-ink-muted text-sm font-mono px-3 py-6">Loading coaching context…</p>
            ) : devQuery.isError ? (
              <p className="text-destructive font-mono text-sm px-3 py-6">Could not load the coaching context.</p>
            ) : devQuery.data ? (
              <PlayerDevelopmentProfile profile={devQuery.data} viewer={false} presentation="coach-workspace" workspaceSection="context" />
            ) : null}
          </div>
        </div>
      </CoachModal>
    </>
  );
}
